import { ReactElement, ReactNode } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** de-DE with 2 decimals; `undefined`/`null` render as an em dash. */
export const formatLoadTableNumber = (value: number | undefined | null): string =>
  value == null ? '–' : numberFormatter.format(value)

const HeaderWithUnit = ({ label, unit }: { label: ReactNode; unit: string }): ReactElement => (
  // Two-line label-over-unit stack, left-aligned like the cell values below it.
  <Stack direction="column" spacing={0} alignItems="flex-start" sx={{ lineHeight: 1.2 }}>
    <Typography sx={{ fontSize: 12 }}>{label}</Typography>
    <Typography sx={{ fontSize: 12 }}>[{unit}]</Typography>
  </Stack>
)

/**
 * Numeric column with de-DE formatting; raw Newton/meter values keep sorting and filtering exact.
 * `plainLabel` is the text version of the rendered header — the filter panel and column menus
 * fall back to the raw field name without a `headerName`.
 */
export const numberColumn = (
  field: string,
  label: ReactNode,
  plainLabel: string,
  unit: string,
  minWidth = 70,
): GridColDef => ({
  field,
  headerName: `${plainLabel} [${unit}]`,
  type: 'number',
  flex: 1,
  minWidth,
  headerAlign: 'left',
  align: 'left',
  renderHeader: () => <HeaderWithUnit label={label} unit={unit} />,
  valueFormatter: (value: number | undefined) => formatLoadTableNumber(value),
})

export const textColumn = (
  field: string,
  headerName: string,
  flex = 1,
  minWidth = 70,
): GridColDef => ({
  field,
  headerName,
  flex,
  minWidth,
})

/** Load category short label ("G", "W", …) — the "Kat." column of the element load tables. */
export const categoryColumn: GridColDef = {
  field: 'category',
  headerName: 'Kat.',
  width: 50,
  minWidth: 50,
  headerAlign: 'center',
  align: 'center',
}

/**
 * "Quelle" column in the look of the element load tables' `LoadSourceLabel`: the source rendered
 * as a (non-clickable) button label, "extern" when the load has no named source. The load DTOs
 * carry only the preformatted label — no source guid — so there is nothing to navigate to.
 */
export const sourceColumn: GridColDef = {
  field: 'origin',
  headerName: 'Quelle',
  flex: 1.4,
  minWidth: 100,
  renderCell: (params: GridRenderCellParams) => (
    <Button
      size="small"
      sx={{
        fontSize: 'inherit',
        textTransform: 'none',
        cursor: 'default',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {params.value}
    </Button>
  ),
}

/** Distinct origins of combined rows, joined like "Decke 00.D01, Unterzug 00.U03". */
export const mergeOrigins = (a: string, b: string): string =>
  Array.from(
    new Set([...a.split(', '), ...b.split(', ')].filter(part => part && part !== 'extern')),
  ).join(', ') || 'extern'

/** "Lasten zusammenfassen": merge rows sharing a key (load case + position), summing via `merge`. */
export const combineRowsBy = <T extends { id: number }>(
  rows: T[],
  keyOf: (row: T) => string,
  merge: (a: T, b: T) => T,
): T[] => {
  const combined = new Map<string, T>()
  rows.forEach(row => {
    const key = keyOf(row)
    const existing = combined.get(key)
    combined.set(key, existing ? merge(existing, row) : row)
  })
  return Array.from(combined.values()).map((row, index) => ({ ...row, id: index }))
}

/** Relieving point/line loads live on the wall, so Position/Start/Ende are wall-relative. */
export const WALL_LOAD_POSITION_DESCRIPTION =
  'Die Positionsangaben beziehen sich auf die Wand, da es sich um die Lasten auf der Wand handelt.'
